import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AccidentListTable.module.scss';
import { DataTableWithSidebar, DataTableColumn, OrderData } from 'common/components/dataTable';
import { AccidentDto } from 'api/accidents/models/AccidentDto';
import { AccidentsSearchCriteria } from 'api/accidents/models/AccidentsSearchCriteria';
import { BLAMING_COLORS, DATE_FORMAT_DEFAUT, DefaultAssetImage, LOGGER_LOG_TYPE } from 'Config';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { FaEllipsisH } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import AccidentsService from 'api/accidents/AccidentsService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';

type AccidentTableRow = AccidentDto & {
    driverDisplay: string;
    dateDisplay: string;
    locationDisplay: string;
    guiltyDisplay: string;
    insuranceContractDisplay: string;
    insuranceNumberDisplay: string;
    insuranceAssumedCostDisplay: string;
    vehicleRegistration: string;
    vehicleBrand: string;
    vehicleModel: string;
    vehicleImage: string;
};

export type Props = {
    items: AccidentDto[];
    totalItems: number;
    isLoadingMore: boolean;
    hasMore: boolean;
    isVehicle?: boolean;
    isDriver?: boolean;
    editable: boolean;
    onLoadMore: () => void;
    onTableFilter: (field: string, isFilterAsc: boolean) => void;
    onClickItem: (item: AccidentDto, isDetails: boolean) => void;
    onDeleteFromList: () => void;
    criteria: AccidentsSearchCriteria;
    reRenderSearch: number;
    scrollToRowId?: string | null;
    hasMorePrevious?: boolean;
    isLoadingPrevious?: boolean;
    onLoadPrevious?: () => void;
    onFetchAllForExport?: () => Promise<AccidentDto[]>;
    exportRef?: React.MutableRefObject<{ exportExcel: () => Promise<void> } | null>;
};

const AccidentListTable = ({
    items,
    isLoadingMore,
    hasMore,
    isVehicle,
    isDriver,
    editable,
    onLoadMore,
    onTableFilter,
    onClickItem,
    onDeleteFromList,
    criteria,
    reRenderSearch,
    scrollToRowId,
    hasMorePrevious,
    isLoadingPrevious,
    onLoadPrevious,
    onFetchAllForExport,
    exportRef,
}: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const { vehicleId, driverId, tabId } = useParams<{ vehicleId: string; driverId: string; tabId: string }>();

    const imageCacheKey = useRef(new Date().getTime());
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasAccidentsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_ACCIDENTS_WRITE']);

    const [itemToRemove, setItemToRemove] = useState<AccidentDto | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);

    const showRemoveItemDialog = (item: AccidentDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            Loading.show();
            await AccidentsService.remove(itemToRemove);
            onCancelRemove();
            onDeleteFromList();
            addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete accident`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            Loading.hide();
        }
    };

    const renderTableActionCell = (row: AccidentTableRow) => {
        const urlDetails = (isVehicle || isDriver)
            ? `/${isDriver ? 'drivers' : 'vehicles'}/details/${isDriver ? driverId : vehicleId}/${tabId || 'accidents'}/details/${row.id}`
            : `accidents/details/${row.id}`;
        const urlEdit = (isVehicle || isDriver)
            ? `/${isDriver ? 'drivers' : 'vehicles'}/details/${isDriver ? driverId : vehicleId}/${tabId || 'accidents'}/edit/${row.id}`
            : `accidents/edit/${row.id}`;

        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={urlDetails}>
                            {t('common.details')}
                        </DropdownItem>
                        {hasAccidentsWritePolicy && editable && <DropdownItem url={urlEdit}>
                            {t('common.edit')}
                        </DropdownItem>}
                        {hasAccidentsWritePolicy && editable && <DropdownItem onClick={() => showRemoveItemDialog(row)}>
                            {t('common.remove')}
                        </DropdownItem>}
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const tableColumns: DataTableColumn<AccidentTableRow>[] = useMemo(() => {
        const cols: DataTableColumn<AccidentTableRow>[] = [
            {
                field: 'driverDisplay',
                name: t('accidents.list.driver') as string,
                orderColumn: 'anu.real_name',
                width: 180,
                exportValue: row => row.driverName ?? '',
                renderCell: row =>
                    <AvatarWithText src={row.driverPhotoUrl}
                        size="x2"
                        hideAvatar={!row.driverName}>
                        <div className={styles.bold}>{row.driverName || '-'}</div>
                    </AvatarWithText>,
            },
            {
                field: 'dateDisplay',
                name: t('accidents.list.date') as string,
                orderColumn: 'a.date',
                width: 130,
                exportValue: row => row.date ? moment(row.date).format(DATE_FORMAT_DEFAUT) + (row.hour ? ' | ' + row.hour : '') : '',
                renderCell: row =>
                    <>{moment(row.date).format(DATE_FORMAT_DEFAUT) + (row.hour ? (' | ' + row.hour) : '')}</>,
            },
            {
                field: 'locationDisplay',
                name: t('accidents.list.location') as string,
                orderColumn: 'location',
                width: 150,
                hideOn: ['sm', 'md'],
                renderCell: row => <>{row.location || '-'}</>,
            },
            {
                field: 'guiltyDisplay',
                name: t('accidents.list.guilt') as string,
                orderColumn: 'guilty',
                width: 100,
                exportValue: row => t(`accidents.guilt_types.${row.guilty ? 'guilty' : 'innocent'}` as any) as string,
                renderCell: row =>
                    <div className={styles.statusColumn}
                        style={{ color: row.guilty ? BLAMING_COLORS.GUILTY : BLAMING_COLORS.NOT_GUILTY }}>
                        <span>{t(`accidents.guilt_types.${row.guilty ? 'guilty' : 'innocent'}` as any)}</span>
                    </div>,
            },
            {
                field: 'insuranceContractDisplay',
                name: t('accidents.list.insurance_contract') as string,
                orderColumn: 's.name',
                width: 180,
                hideOn: ['sm', 'md'],
                exportValue: row => row.insuranceContractSupplier ? `${row.insuranceContractSupplier}, ${t('accidents.list.insurance')}` : '-',
                renderCell: row =>
                    <>{row.insuranceContractSupplier ? `${row.insuranceContractSupplier}, ${t('accidents.list.insurance')}` : '-'}</>,
            },
            {
                field: 'insuranceNumberDisplay',
                name: t('accidents.list.insurance_number') as string,
                orderColumn: 'ic.number',
                width: 120,
                hideOn: ['sm', 'md'],
                renderCell: row =>
                    <>{row.insuranceContractNumber || '-'}</>,
            },
            {
                field: 'insuranceAssumedCostDisplay',
                name: t('accidents.list.insurance_assumed_cost') as string,
                hideOrderButton: true,
                width: 120,
                hideOn: ['sm', 'md'],
                exportValue: row => t(`common.${row.insuranceAssumedCost ? 'yes' : 'no'}` as any) as string,
                renderCell: row =>
                    <>{t(`common.${row.insuranceAssumedCost ? 'yes' : 'no'}` as any)}</>,
            },
        ];

        if (!isVehicle) {
            cols.unshift(
                {
                    field: 'vehicleImage',
                    name: '' as string,
                    hideOrderButton: true,
                    width: 75,
                    hideOn: ['sm', 'md'],
                    excludeFromExport: true,
                    renderCell: row =>
                        <div className={styles.imageDescCell}>
                            <div className={styles.contentImage}>
                                <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: row?.vehiclePhotoUrl ? 'url(' + row?.vehiclePhotoUrl + '?_=' + imageCacheKey.current + ')' : row?.vehicleSegmentDefaultPhotoUrl ? 'url(' + row?.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />
                            </div>
                        </div>,
                },
                {
                    field: 'vehicleRegistration',
                    name: t('charges.list.vehicle') as string,
                    orderColumn: 'v.registration_number',
                    width: 130,
                    renderCell: row =>
                        <div className={styles.descCell}>
                            <div className={styles.columnPlate}>{row.vehicleRegistrationNumber}</div>
                        </div>,
                },
                {
                    field: 'vehicleBrand',
                    name: t('car_verification.list.brand') as string,
                    orderColumn: 'vb.name',
                    width: 100,
                    hideOn: ['sm'],
                    renderCell: row =>
                        <div className={styles.columnBrand}>
                            {row.vehicleBrandName || '-'}
                        </div>,
                },
                {
                    field: 'vehicleModel',
                    name: t('car_verification.list.model') as string,
                    orderColumn: 'vm.name',
                    width: 100,
                    hideOn: ['sm'],
                    renderCell: row =>
                        <div className={styles.columnBrand}>
                            {row.vehicleModelName || '-'}
                        </div>,
                }
            );
        }

        return cols;
    }, [isVehicle]);

    const toTableRow = (item: AccidentDto): AccidentTableRow => ({
        ...item,
        driverDisplay: item.driverName || '',
        dateDisplay: item.date ? moment(item.date).format(DATE_FORMAT_DEFAUT) : '',
        locationDisplay: item.location || '',
        guiltyDisplay: item.guilty ? 'guilty' : 'innocent',
        insuranceContractDisplay: item.insuranceContractSupplier || '',
        insuranceNumberDisplay: item.insuranceContractNumber || '',
        insuranceAssumedCostDisplay: item.insuranceAssumedCost ? 'yes' : 'no',
        vehicleRegistration: item.vehicleRegistrationNumber || '',
        vehicleBrand: item.vehicleBrandName || '',
        vehicleModel: item.vehicleModelName || '',
        vehicleImage: item.vehiclePhotoUrl || '',
    });

    const tableRows: AccidentTableRow[] = useMemo(() => {
        return items.map(toTableRow);
    }, [items]);

    const fetchAllForExcelExport = useCallback(async (): Promise<AccidentTableRow[]> => {
        if (!onFetchAllForExport) return [];
        const allAccidents = await onFetchAllForExport();
        return allAccidents.map(toTableRow);
    }, [onFetchAllForExport]);

    const handleOrderChange = (orderData: OrderData<AccidentTableRow>) => {
        const orderColumn = orderData.orderColumn || (orderData.field as string);
        onTableFilter(orderColumn, orderData.isOrderAsc);
    };

    const getFieldFromOrderColumn = (orderColumn: string | undefined): keyof AccidentTableRow => {
        const mapping: Record<string, keyof AccidentTableRow> = {
            'anu.real_name': 'driverDisplay',
            'a.date': 'dateDisplay',
            'location': 'locationDisplay',
            'guilty': 'guiltyDisplay',
            's.name': 'insuranceContractDisplay',
            'ic.number': 'insuranceNumberDisplay',
            'v.registration_number': 'vehicleRegistration',
            'vb.name': 'vehicleBrand',
            'vm.name': 'vehicleModel',
        };
        return mapping[orderColumn ?? ''] ?? 'dateDisplay';
    };

    const defaultOrder: OrderData<AccidentTableRow> = useMemo(() => {
        const orderColumn = criteria?.orderColumn ?? 'a.date';
        return {
            field: getFieldFromOrderColumn(orderColumn),
            orderColumn: orderColumn,
            isOrderAsc: criteria?.orderBy === 'asc',
        };
    }, [criteria?.orderColumn, criteria?.orderBy]);

    const getRowHref = (row: AccidentTableRow) => {
        if (isVehicle || isDriver) {
            return `/${isDriver ? 'drivers' : 'vehicles'}/details/${isDriver ? driverId : vehicleId}/${tabId || 'accidents'}/details/${row.id}`;
        }
        return `accidents/details/${row.id}`;
    };

    return (
        <div className={styles.tableContainer}>
            <DataTableWithSidebar
                key={`${reRenderSearch}`}
                cacheKey={`accident-list-v2-${isVehicle ? vehicleId : isDriver ? driverId : 'all'}`}
                columns={tableColumns}
                rows={tableRows}
                defaultOrder={defaultOrder}
                onRowClick={row => onClickItem(row, true)}
                onOrderChange={handleOrderChange}
                onRenderAction={renderTableActionCell}
                onHref={getRowHref}
                onRowId={row => row.id ?? ''}
                scrollToRowId={scrollToRowId}
                infiniteScroll={{
                    isLoading: isLoadingMore,
                    hasMore: hasMore,
                    onLoadMore: onLoadMore,
                    hasMorePrevious: hasMorePrevious,
                    isLoadingPrevious: isLoadingPrevious,
                    onLoadPrevious: onLoadPrevious,
                }}
                exportExcel={onFetchAllForExport ? {
                    fetchAll: fetchAllForExcelExport,
                    fileName: t('accidents.title') as string,
                } : undefined}
                exportRef={exportRef}
                hideExportInSidebar={!!exportRef}
            />
            <QuestionYesNo
                onNo={onCancelRemove}
                onYes={onRemove}
                isVisible={showRemoveModal}
                message={t('common.messages.remove_record')} />
        </div>
    );
};

export default AccidentListTable;
